import {
  DiscountedPrice,
  DiscountRow,
  OriginalPrice,
  PriceAmount,
  PriceLabel,
  Wrapper,
} from './PriceDisplay.styles';
import type {
  DiscountPriceDisplayProps,
  FromPriceDisplayProps,
  PriceDisplayProps,
} from './PriceDisplay.types';

function formatCurrency(
  amount: number,
  currencyCode: string,
  locale?: string,
  noPennies?: boolean
): string {
  const majorUnits = amount / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: noPennies ? 0 : 2,
      maximumFractionDigits: noPennies ? 0 : 2,
    }).format(majorUnits);
  } catch {
    return `${currencyCode} ${majorUnits.toFixed(2)}`;
  }
}

export function PriceDisplay({
  amount,
  currencyCode,
  label,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: PriceDisplayProps) {
  const guests = numberOfGuests || 1;
  const formatted = formatCurrency(amount * guests, currencyCode, locale, noPennies);

  if (label) {
    return (
      <Wrapper>
        <PriceLabel $size={size}>{label}</PriceLabel>
        <PriceAmount $size={size}>{formatted}</PriceAmount>
      </Wrapper>
    );
  }

  return <PriceAmount $size={size}>{formatted}</PriceAmount>;
}

export function DiscountPriceDisplay({
  price,
  discount,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: DiscountPriceDisplayProps) {
  const guests = numberOfGuests || 1;
  const discountPrice = discount?.discountedPrice;
  const hasDiscount = discountPrice && discountPrice.amount !== price.amount;

  if (!hasDiscount) {
    return (
      <PriceDisplay
        amount={price.amount}
        currencyCode={price.currencyCode}
        locale={locale}
        noPennies={noPennies}
        numberOfGuests={numberOfGuests}
        size={size}
      />
    );
  }

  const formattedOriginal = formatCurrency(
    price.amount * guests,
    price.currencyCode,
    locale,
    noPennies
  );
  const formattedDiscount = formatCurrency(
    discountPrice.amount * guests,
    discountPrice.currencyCode,
    locale,
    noPennies
  );

  return (
    <DiscountRow>
      <OriginalPrice $size={size}>{formattedOriginal}</OriginalPrice>
      <DiscountedPrice $size={size}>{formattedDiscount}</DiscountedPrice>
    </DiscountRow>
  );
}

export function FromPriceDisplay({
  amount,
  currencyCode,
  label = 'From',
  discount,
  locale,
  noPennies,
  numberOfGuests,
  size = 'medium',
}: FromPriceDisplayProps) {
  return (
    <Wrapper>
      <PriceLabel $size={size}>{label}</PriceLabel>
      <DiscountPriceDisplay
        price={{ amount, currencyCode }}
        discount={discount}
        locale={locale}
        noPennies={noPennies}
        numberOfGuests={numberOfGuests}
        size={size}
      />
    </Wrapper>
  );
}
